import type {
  ExecutionCharter,
} from "./contracts";

import type {
  MissionControl,
} from "./missionControl";

import type {
  OperationalTakeoverPreparation,
} from "../cyan/orchestration/operationalTakeoverTypes";

export interface MissionProgramAuthorizationInput {
  readonly missionId: string;
  readonly preparation: OperationalTakeoverPreparation;

  readonly requestedBy: string;

  readonly conversationId?: string;
  readonly projectId?: string;
  readonly repository?: string;
}

export interface MissionProgramAuthorization {
  readonly missionId: string;
  readonly charter: ExecutionCharter;
}

/**
 * The control-plane boundary between owner-confirmed intent
 * and durable execution.
 *
 * Mission Control accepts the approved charter BEFORE the
 * corresponding ExecutionProgram is persisted.
 */
export class MissionProgramAuthority {
  constructor(
    private readonly missionControl:
      MissionControl,
  ) {}

  async authorize(
    input: MissionProgramAuthorizationInput,
  ): Promise<MissionProgramAuthorization> {
    const workPackages =
      input.preparation.workPackages;

    if (!workPackages.length) {
      throw new Error(
        "MISSION_PROGRAM_AUTHORITY_NO_APPROVED_WORK",
      );
    }

    const scope = [
      ...new Set(
        workPackages.flatMap(
          item =>
            item.resourceScope.paths,
        ),
      ),
    ];

    const acceptanceCriteria = [
      ...new Set(
        workPackages.flatMap(
          item =>
            item.definitionOfDone,
        ),
      ),
    ];

    const requiredCapabilities = [
      ...new Set(
        workPackages.flatMap(
          item => [
            item.department,
            item.agentRole,
            ...item.resourceScope.services,
            ...item.resourceScope.integrations,
          ],
        ),
      ),
    ];

    const charter: ExecutionCharter = {
      missionId:
        input.missionId,

      status:
        "APPROVED",

      ownerIntent:
        input.preparation.objective,

      objective:
        input.preparation.objective,

      acceptanceCriteria:
        acceptanceCriteria.length
          ? acceptanceCriteria
          : [
              "All approved work packages reach verified completion.",
            ],

      scope,

      outOfScope: [
        ...input.preparation.blocked,
        ...input.preparation.approvalRequired,
      ],

      constraints: [
        "Execute only owner-confirmed work.",
        "Respect WorkPackage resource scopes and authorities.",
        "Preserve durable execution evidence.",
        "Technical recovery does not require Owner relay unless an explicit owner-only boundary is reached.",
      ],

      businessRules: [],

      riskPolicy: {
        reversibleExecutionOnly: true,
        ownerApprovalAlreadyConfirmed: true,
      },

      ownerPreauthorizations: [
        "READ",
        "WORKSPACE_WRITE",
        "TEST_EXECUTION",
        "GIT_LOCAL",
      ],

      ownerOnlyBoundaries: [
        "irreversible destructive action",
        "new external expenditure",
        "credential ownership change",
        "production data destruction",
        "material scope expansion",
      ],

      projects:
        input.projectId
          ? [input.projectId]
          : [],

      repositories:
        input.repository
          ? [input.repository]
          : [],

      products: [
        "Franca SS",
      ],

      requiredCapabilities,

      finalReportingContract: {
        returnTo:
          "Cyan",

        requestedBy:
          input.requestedBy,

        conversationId:
          input.conversationId ?? null,

        requireEvidence:
          true,

        requireFinalState:
          true,
      },
    };

    await this.missionControl.accept(
      charter,
    );

    return {
      missionId:
        input.missionId,

      charter,
    };
  }
}
